// SB6-ANA-POP-01 — keep the popped-out Viewer in lockstep with the main
// window over the existing BroadcastChannel (`popoutSync`).
//
// A live OpenSeadragon canvas cannot be moved across windows, so the child
// boots a *fresh* Viewer. For it to behave like the in-page Viewer it must
// mirror three things from the main window:
//   - the **active image** (so it shows the right picture),
//   - the **selection** (so the right bbox is highlighted), and
//   - the user's **preferences** (max-zoom, bounding-box colours, …) — the
//     child has its OWN TanStack Query cache, so a Settings change in the
//     main window doesn't reach it unless we forward it.
//
// This hook runs in BOTH windows. It APPLIES incoming messages to local
// state (guard-first so applying doesn't echo back) and POSTS local changes
// to the other window. Image/selection are bidirectional; prefs only ever
// change in the main window (the pop-out has no Settings UI) so they flow
// one-way main → child.
//
// A child that opens *after* the main window already advanced would miss the
// main's pushes (BroadcastChannel does not buffer). So the child posts
// `requestSync` once its channel is installed, and the main answers with its
// current image + selection + prefs — guaranteeing the popped Viewer is
// correct immediately, without the user nudging anything.
//
// Sync only runs while a pop-out exists (or we ARE the pop-out) so the main
// analysis page stays quiet in the common single-window case.
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createSyncChannel, type SyncChannel } from "./popoutSync.js";
import { usePopoutWorkspaceStore } from "./popoutWorkspaceStore.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useSelectionStore } from "../state/selectionStore.js";
import {
  lysePreferencesKeys,
  useLysePreferencesQuery,
} from "../../preferences/queries.js";

export interface UseViewerPopoutSyncArgs {
  readonly inspectionId: string;
  /** True in the popped-out child window (drives the late-open re-push). */
  readonly isPopout: boolean;
}

export function useViewerPopoutSync({
  inspectionId,
  isPopout,
}: UseViewerPopoutSyncArgs): void {
  const activeImageId = useImageActivationStore((s) => s.activeImageId);
  const setActiveImage = useImageActivationStore((s) => s.setActive);
  const activeFindingId = useSelectionStore((s) => s.activeFindingId);
  const setActiveSelection = useSelectionStore((s) => s.setActive);
  const popoutCount = usePopoutWorkspaceStore((s) => s.windows.length);
  const { prefs, isPending: prefsPending } = useLysePreferencesQuery();
  const queryClient = useQueryClient();

  const enabled = isPopout || popoutCount > 0;

  const channelRef = useRef<SyncChannel | null>(null);
  // `undefined` = not yet initialised (skip the first post); thereafter the
  // last value we sent or received, so an applied value doesn't echo back.
  const lastImageRef = useRef<string | null | undefined>(undefined);
  const lastSelRef = useRef<string | null | undefined>(undefined);
  const lastPrefsRef = useRef<unknown>(undefined);
  // Latest local values, for the re-push on child open (`requestSync`).
  const latestImageRef = useRef<string | null>(activeImageId);
  const latestSelRef = useRef<string | null>(activeFindingId);
  const latestPrefsRef = useRef<unknown>(prefs);
  latestImageRef.current = activeImageId;
  latestSelRef.current = activeFindingId;
  latestPrefsRef.current = prefs;

  // Install the channel + apply incoming messages.
  useEffect(() => {
    if (!enabled || !inspectionId) return;
    const ch = createSyncChannel({ dataAcquisitionId: inspectionId });
    channelRef.current = ch;
    const off = ch.addListener((msg) => {
      if (msg.type === "image") {
        lastImageRef.current = msg.activeImageId;
        setActiveImage(msg.activeImageId);
      } else if (msg.type === "selection") {
        lastSelRef.current = msg.activeFindingId;
        setActiveSelection(msg.activeFindingId, null);
      } else if (msg.type === "prefs" && isPopout) {
        // Settings changed in the main window — write the new prefs into
        // this child's cache so the Viewer recolours / re-clamps live.
        lastPrefsRef.current = msg.prefs;
        queryClient.setQueryData(lysePreferencesKeys.current(), {
          prefs: msg.prefs,
          isFromServer: true,
          isFetchError: false,
        });
      } else if (msg.type === "requestSync" && !isPopout) {
        // A child just booted and asked for current state — answer with our
        // latest image + selection + prefs so it hydrates immediately.
        ch.postMessage({
          type: "image",
          activeImageId: latestImageRef.current,
        });
        ch.postMessage({
          type: "selection",
          activeFindingId: latestSelRef.current,
        });
        ch.postMessage({ type: "prefs", prefs: latestPrefsRef.current });
      }
    });
    // Child side: as soon as we're listening, ask the main window for state.
    if (isPopout) {
      ch.postMessage({ type: "requestSync" });
    }
    return () => {
      off();
      ch.close();
      channelRef.current = null;
    };
  }, [
    enabled,
    inspectionId,
    isPopout,
    setActiveImage,
    setActiveSelection,
    queryClient,
  ]);

  // Post local image changes.
  useEffect(() => {
    if (!enabled) return;
    if (lastImageRef.current === undefined) {
      lastImageRef.current = activeImageId; // initialise; don't post on mount
      return;
    }
    if (lastImageRef.current === activeImageId) return; // applied / unchanged
    lastImageRef.current = activeImageId;
    channelRef.current?.postMessage({ type: "image", activeImageId });
  }, [enabled, activeImageId]);

  // Post local selection changes.
  useEffect(() => {
    if (!enabled) return;
    if (lastSelRef.current === undefined) {
      lastSelRef.current = activeFindingId;
      return;
    }
    if (lastSelRef.current === activeFindingId) return;
    lastSelRef.current = activeFindingId;
    channelRef.current?.postMessage({ type: "selection", activeFindingId });
  }, [enabled, activeFindingId]);

  // Post preference changes — main window only (the pop-out has no Settings
  // UI, so prefs flow one-way). Skipped while the prefs GET is still pending
  // (the value is a fresh factory-defaults object each render until then).
  useEffect(() => {
    if (!enabled || isPopout || prefsPending) return;
    if (lastPrefsRef.current === undefined) {
      lastPrefsRef.current = prefs; // initialise; don't post on mount
      return;
    }
    if (lastPrefsRef.current === prefs) return; // unchanged reference
    lastPrefsRef.current = prefs;
    channelRef.current?.postMessage({ type: "prefs", prefs });
  }, [enabled, isPopout, prefsPending, prefs]);
}
